import { useState, useCallback } from 'react'
import { TagRecommendation } from '@/types'
import apiService from '@/services/api'

export type RecommendationMethod = 'hybrid' | 'embedding_similarity' | 'text_similarity'

export interface UseTagRecommendationsReturn {
  recommendations: TagRecommendation[]
  isLoading: boolean
  error: string | null
  method: RecommendationMethod
  setMethod: (method: RecommendationMethod) => void
  fetchRecommendations: (fileId: number, existingTags: string[]) => Promise<void>
  clearError: () => void
}

export function useTagRecommendations(): UseTagRecommendationsReturn {
  const [recommendations, setRecommendations] = useState<TagRecommendation[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [method, setMethod] = useState<RecommendationMethod>('hybrid')

  const fetchRecommendations = useCallback(
    async (fileId: number, existingTags: string[]) => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await apiService.getTagRecommendations(fileId, method, 15)

        // Filter out existing tags
        const filteredRecommendations = response.recommendations.filter(
          (rec) => !existingTags.includes(rec.tag_name)
        )

        setRecommendations(filteredRecommendations)
      } catch (err: any) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch tag suggestions')
      } finally {
        setIsLoading(false)
      }
    },
    [method]
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    recommendations,
    isLoading,
    error,
    method,
    setMethod,
    fetchRecommendations,
    clearError,
  }
}
